import { faArrowCircleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React from "react";
import { BiMessageRoundedMinus } from "react-icons/bi";
import { VscCheck, VscCheckAll } from "react-icons/vsc";
import {
  findExtension,
  parseButtonText,
  selectFileImage,
} from "../helpers/utils";
import {
  FileType,
  MsgType,
  keyboardObject,
} from "../types/webchatConversation";
import ChatItemButton from "./ChatItemButton";
import ChatItemCarousel from "./ChatItemCarousel";

interface IProps {
  userName: string;
  text: string | undefined;
  avatar: string;
  placement: string;
  time: string;
  date: string;
  type: string;
  media?: string;
  last?: boolean;
  keyboard?: keyboardObject;
  notDelivered?: boolean;
  sentMsg?: boolean;
  printUsername?: boolean;
  msg?: any;
}

interface ChatItemCarouselProps {
  richMedia: any; // Replace 'any' with a proper type if available
}

/* AVATAR */
const Avatar: React.FC<any> = ({ userAvatar, postedOn, datePosted }) => (
  <div className="chat-avatar">
    <img src={userAvatar} alt={userAvatar} />
    <i>{postedOn}</i>
    <br />
    <i className="date-posted">{datePosted}</i>
  </div>
);

/* CHAT ITEM - TEXT */
const ChatItemText: React.FC<any> = ({
  userName,
  text,
  notDelivered,
  printUsername,
  placement,
  postedOn,
  sentMsg,
}) => {
  if (!text || typeof text !== "string" || text.trim() === "") return null;
  return (
    <div
      className={`conversation-text ${
        !printUsername && placement === "left"
          ? "conversation-text-no-username"
          : ""
      }`}
    >
      <div
        className={`${
          !printUsername ? "ctext-wrap-no-username" : "ctext-wrap"
        } ${notDelivered ? "ctext-wrap-not-delivered" : ""}`}
      >
        {printUsername && <i style={{ wordBreak: "break-all" }}>{userName}</i>}
        <p className="chat-text-wrap">{text}</p>
        <div
          className="date-time"
          style={{
            position: "absolute",
            bottom: "2px",
            right: "5px",
            color: notDelivered ? "red" : "#36454F",
          }}
        >
          {(!printUsername || placement !== "left") && <>{postedOn}</>}
          {placement === "right" && (
            <span style={{ marginLeft: "8px" }}>
              {notDelivered ? (
                <BiMessageRoundedMinus style={{ fontSize: "20px" }} />
              ) : sentMsg ? (
                <VscCheck />
              ) : (
                <VscCheckAll />
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* CHAT ITEM - MEDIA */
const ChatItemMedia: React.FC<any> = ({ userName, media, type }) => (
  <div className="conversation-media">
    <div className="cmedia-wrap">
      <i>{userName}</i>
      {type === MsgType.PICTURE && (
        <img className="responsive-width" src={media} alt={`${userName}Img`} />
      )}
      {type === MsgType.VIDEO && (
        <video className="responsive-width" src={media} controls />
      )}
      {type === MsgType.AUDIO && <audio src={media} controls />}
    </div>
  </div>
);

/* CHAT ITEM - FILE */
const ChatItemFile: React.FC<any> = ({
  userName,
  image,
  type,
  media,
  fileName,
}) => {
  const [attHover, setAttHover] = React.useState("");
  return (
    <div className="conversation-media">
      <div className="cmedia-wrap">
        <i>{userName}</i>
        <a
          className={classNames({ attachment_arrow_hover: attHover === media })}
          download={fileName || "downloaded-file"}
          href={media}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div
            className="d-flex justify-content-center align-items-end"
            onMouseEnter={() => setAttHover(media)}
            onMouseLeave={() => setAttHover("")}
          >
            <img
              style={{ maxWidth: "80px", maxHeight: "120px" }}
              src={image}
              alt={`${userName}.${type}`}
              className="fileIcon"
            />
            <FontAwesomeIcon
              className={classNames({
                fileDownload: attHover !== media,
                fileDownloadSelected: attHover === media,
              })}
              icon={faArrowCircleDown}
              size="2x"
            />
          </div>
        </a>
      </div>
    </div>
  );
};

/* Chat Item */
const ChatItem: React.FC<IProps> = ({
  userName,
  text,
  avatar,
  placement,
  time,
  date,
  type,
  media,
  last,
  keyboard,
  notDelivered,
  sentMsg,
  printUsername,
  msg,
}) => {
  // Koristi media polje za URL (koje CC vraća za agent poruke)
  const fileUrl = media || msg?.file?.url;
  const fileName = msg?.fileName || msg?.file?.fileName;

  const extension = type === MsgType.FILE ? findExtension(fileUrl) : type;

  // Detektuj da li je slika na osnovu tipa, ekstenzije ili mimeType
  const isImage =
    type === MsgType.PICTURE ||
    (fileUrl && /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileUrl)) ||
    (msg?.file?.mimeType && msg.file.mimeType.startsWith("image/"));

  const itemType =
    extension === FileType.MP3 ||
    extension === FileType.WAV ||
    type === MsgType.AUDIO
      ? MsgType.AUDIO
      : isImage
      ? MsgType.PICTURE
      : type;
  const fileImage = selectFileImage(extension);
  return (
    <>
      <li
        className={classNames("clearfix", "chat-item-li", {
          odd: placement !== "left",
        })}
      >
        {printUsername && placement === "left" && (
          <Avatar userAvatar={avatar} postedOn={time} datePosted={date} />
        )}
        {type === "rich_media" && msg?.rich_media?.Buttons ? (
          <ChatItemCarousel richMedia={msg.rich_media} />
        ) : (
          <ChatItemText
            userName={userName}
            text={text}
            notDelivered={placement === "right" && notDelivered}
            sentMsg={sentMsg}
            printUsername={printUsername}
            placement={placement}
            postedOn={time}
            datePosted={date}
          />
        )}
        {(itemType === MsgType.PICTURE ||
          itemType === MsgType.VIDEO ||
          itemType === MsgType.AUDIO) && (
          <ChatItemMedia
            userName={userName}
            media={fileUrl}
            type={itemType}
            notDelivered={placement === "right" && notDelivered}
            printUsername={printUsername}
          />
        )}
        {itemType === MsgType.FILE && (
          <ChatItemFile
            userName={userName}
            image={fileImage}
            type={type}
            media={fileUrl}
            fileName={fileName}
            notDelivered={placement === "right" && notDelivered}
            printUsername={printUsername}
          />
        )}
      </li>
      {last && (
        <li className="li-keyboard">
          {keyboard?.Buttons.map((button, index) => {
            const btnText = parseButtonText(button.Text);
            return (
              <ChatItemButton
                key={index}
                text={btnText}
                value={button.ActionBody}
              />
            );
          })}
        </li>
      )}
    </>
  );
};

export default ChatItem;
